import CreateLeadForm from '@/components/features/Leads/forms/CreateLeadForm';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import type { EditLeadModalProps } from '@/types/forms/leads';
import { router, usePage } from '@inertiajs/react';
import { Edit } from 'lucide-react';
import { cloneElement, isValidElement, useState, type MouseEvent } from 'react';

/**
 * Editing a lead, in a sheet over the lead it belongs to.
 *
 * The body is the same form the create page renders, so the two never drift —
 * only the container differs. This used to be a separate ~500-line component
 * with fewer fields, a different owner picker and its own validation, so a lead
 * created one way could not be fully edited the other.
 */
export default function EditLeadModal({ lead, people, users, trigger, open: openProp, onOpenChange }: EditLeadModalProps) {
    const { t } = useTranslations();
    const { props } = usePage<{ auth?: { user?: { id: string } } }>();

    // Controlled when the caller owns the open state — the lead page does, so it
    // can close its actions sheet before this one opens.
    const [internalOpen, setInternalOpen] = useState(false);
    const isOpen = openProp ?? internalOpen;

    const handleOpenChange = (newOpen: boolean) => {
        if (isOpen === newOpen) {
            return;
        }

        setInternalOpen(newOpen);
        onOpenChange?.(newOpen);
    };

    const renderTrigger = () => {
        if (!trigger) {
            return (
                <ResponsiveModalTrigger asChild>
                    <Button variant="outline">
                        <Edit className="mr-2 h-4 w-4" />
                        {t('leads.forms.edit.buttons.edit_lead', 'Edit Lead')}
                    </Button>
                </ResponsiveModalTrigger>
            );
        }

        if (!isValidElement(trigger)) {
            return (
                <button type="button" onClick={() => handleOpenChange(true)}>
                    {trigger}
                </button>
            );
        }

        type TriggerEvent = MouseEvent<HTMLElement> | Event;
        type TriggerProps = {
            onClick?: (event: TriggerEvent) => void;
            onSelect?: (event: TriggerEvent) => void;
        };

        const triggerElement = trigger as React.ReactElement<TriggerProps>;
        const triggerProps = triggerElement.props;

        const openModal = (event: TriggerEvent) => {
            // Both are guarded: a dropdown item fires onSelect with a plain Event,
            // a button fires onClick with a synthetic one.
            if (event && typeof event === 'object' && 'preventDefault' in event) {
                (event as TriggerEvent & { preventDefault?: () => void }).preventDefault?.();
            }
            if (event && typeof event === 'object' && 'stopPropagation' in event) {
                (event as TriggerEvent & { stopPropagation?: () => void }).stopPropagation?.();
            }

            handleOpenChange(true);
        };

        return cloneElement(triggerElement, {
            onClick: (event: TriggerEvent) => {
                triggerProps.onClick?.(event);
                openModal(event);
            },
            onSelect: (event: TriggerEvent) => {
                triggerProps.onSelect?.(event);
                openModal(event);
            },
        });
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleOpenChange} variant="sheet">
            {renderTrigger()}

            <ResponsiveModalContent size="2xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('leads.forms.edit.title', 'Edit Lead')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>{t('leads.forms.edit.description', 'Update the lead information')}</ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="px-4 py-6 md:px-6">
                    <CreateLeadForm
                        lead={lead}
                        people={people}
                        users={users}
                        // The form only reads currentUser to default a new lead's owner,
                        // which an edit never does — the lead already has one.
                        currentUser={{ id: props.auth?.user?.id ?? '' } as never}
                        compact
                        onCancel={() => handleOpenChange(false)}
                        onSuccess={() => {
                            handleOpenChange(false);
                            router.reload({ only: ['lead'] });
                        }}
                    />
                </ResponsiveModalBody>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
